import React, { forwardRef, useCallback } from 'react'
import { Select as AntSelect } from 'antd'
import {
  difference,
  includes,
  isArray,
  isNil,
  isUndefined,
  map,
  noop,
  size,
} from 'lodash'
import PropTypes from 'prop-types'

import { ALL_SELECT_OPTION_ITEM, SIZES } from '../../constants'
import { antFormItemNamePropType } from '../../prop-types'
import { filterSelectOptions } from '../../utils'

const Select = (
  { value, onChange, name, options, mode, maxSelectableOptions, ...rest },
  ref,
) => {
  const handleOnChange = useCallback(
    selectedValues => {
      if (
        !isNil(maxSelectableOptions) &&
        size(selectedValues) > maxSelectableOptions
      ) {
        return
      }

      if (
        isArray(selectedValues) &&
        includes(selectedValues, ALL_SELECT_OPTION_ITEM.value)
      ) {
        const newItemsSelected = difference(selectedValues, value)

        if (
          size(newItemsSelected) === 1 &&
          size(value) > 0 &&
          includes(newItemsSelected, ALL_SELECT_OPTION_ITEM.value)
        ) {
          onChange([])
        } else {
          const [, ...optionsWithoutAll] = options
          const allValues = map(optionsWithoutAll, 'value')
          onChange(allValues)
        }
      } else {
        onChange(isUndefined(selectedValues) ? null : selectedValues)
      }
    },
    [maxSelectableOptions, onChange, options, value],
  )

  const maxTagPlaceholder = useCallback(
    omittedValues => `and ${size(omittedValues)} more selected`,
    [],
  )

  return (
    <AntSelect
      ref={ref}
      onChange={handleOnChange}
      value={value}
      mode={mode}
      maxTagCount={4}
      maxTagPlaceholder={maxTagPlaceholder}
      filterOption={filterSelectOptions}
      style={{ width: '100%' }}
      dropdownStyle={
        rest.dropdownStyle ? rest.dropdownStyle : { minWidth: 'fit-content' }
      }
      options={options}
      dropdownMatchSelectWidth={SIZES.DROPDOWN_MIN_WIDTH}
      {...rest}
    />
  )
}

Select.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onChange: PropTypes.func,
  name: antFormItemNamePropType.isRequired,
  options: PropTypes.array.isRequired,
  mode: PropTypes.string,
  maxSelectableOptions: PropTypes.number,
}

Select.defaultProps = {
  mode: undefined,
  value: undefined,
  maxSelectableOptions: undefined,
  onChange: noop,
}

export default forwardRef(Select)
